import api from '@/lib/axios';

import type { PaginatedResponse } from '@/types/tenant';
import type { ListFilterPayload } from '@/types/filters';
import type {
  AssignRolesPayload,
  CreateRolePayload,
  ModuleWithPermissions,
  RoleDto,
  RoleSummaryDto,
  TransferSuperAdminPayload,
  UpdateRolePayload,
  UserPermissionsDto,
  UserRoleDto,
} from '@/types/role';

// ============================================
// Query Params
// ============================================

interface GetRolesParams {
  page?: number;
  size?: number;
  sort?: string;
}

// ============================================
// Modules With Permissions (for role editor UI)
// ============================================

export const getModulesWithPermissions = async (): Promise<ModuleWithPermissions[]> => {
  const response = await api.get<ModuleWithPermissions[]>(
    '/platform-service/v1/modules/with-permissions',
  );
  return response.data;
};

// ============================================
// Roles CRUD
// ============================================

export const getRoles = async (
  params: GetRolesParams = {},
  body?: ListFilterPayload,
): Promise<PaginatedResponse<RoleSummaryDto>> => {
  const response = await api.post<PaginatedResponse<RoleSummaryDto>>(
    '/platform-service/v1/roles/list',
    body ?? {},
    { params },
  );
  return response.data;
};

export const getRoleById = async (id: string): Promise<RoleDto> => {
  const response = await api.get<RoleDto>(`/platform-service/v1/roles/${id}`);
  return response.data;
};

export const createRole = async (data: CreateRolePayload): Promise<RoleDto> => {
  const response = await api.post<RoleDto>('/platform-service/v1/roles', data, { successToast: true });
  return response.data;
};

export const updateRole = async (
  id: string,
  data: UpdateRolePayload,
): Promise<RoleDto> => {
  const response = await api.put<RoleDto>(`/platform-service/v1/roles/${id}`, data, { successToast: true });
  return response.data;
};

export const deleteRole = async (id: string): Promise<void> => {
  await api.delete(`/platform-service/v1/roles/${id}`, { successToast: true });
};

// ============================================
// Super Admin Transfer
// ============================================

export const transferSuperAdmin = async (
  data: TransferSuperAdminPayload,
): Promise<void> => {
  await api.post('/platform-service/v1/roles/super-admin/transfer', data, { successToast: true });
};

// ============================================
// User Roles
// ============================================

export const getUserRoles = async (userId: string): Promise<UserRoleDto[]> => {
  const response = await api.get<UserRoleDto[]>(
    `/platform-service/v1/users/${userId}/roles`,
  );
  return response.data;
};

export const assignUserRoles = async (
  userId: string,
  data: AssignRolesPayload,
): Promise<UserRoleDto[]> => {
  const response = await api.post<UserRoleDto[]>(
    `/platform-service/v1/users/${userId}/roles`,
    data,
    { successToast: true },
  );
  return response.data;
};

export const removeUserRole = async (
  userId: string,
  roleId: string,
): Promise<void> => {
  await api.delete(`/platform-service/v1/users/${userId}/roles/${roleId}`, { successToast: true });
};

// ============================================
// User Permissions
// ============================================

export const getUserPermissions = async (
  userId: string,
): Promise<UserPermissionsDto> => {
  const response = await api.get<UserPermissionsDto>(
    `/platform-service/v1/users/${userId}/permissions`,
  );
  return response.data;
};
